import { useRef } from 'react'
import { useTranslation } from 'next-i18next'

import AsyncSelect from 'react-select/async'
import { Loader } from '@googlemaps/js-api-loader'
import { useDebouncedCallback } from 'use-debounce'
import { Address } from '@/types'
import * as Sentry from '@sentry/nextjs'
import { useToast } from '@/hooks/use-toast'

const SEARCH_DEBOUNCE_MS = 300
const PLACE_FIELDS = ['location', 'addressComponents']

type PlaceOption = {
  label: string
  value: google.maps.places.PlacePrediction
}

// The Maps JS loader is a singleton: it must be created once, so the library
// is loaded in the first locale and each request then passes its own language.
let placesLibrary: Promise<google.maps.PlacesLibrary> | undefined

const loadPlacesLibrary = (language: string) => {
  placesLibrary ??= new Loader({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY ?? '',
    language,
  })
    .importLibrary('places')
    .catch((e) => {
      placesLibrary = undefined
      throw e
    })

  return placesLibrary
}

const GoogleAutoComplete = ({
  locale,
  setAddress,
  placeholder,
}: {
  setAddress: (value: Address) => void
  locale: string
  placeholder?: string
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken>()

  const [loadOptions] = useDebouncedCallback(
    async (input: string, callback: (options: PlaceOption[]) => void) => {
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await loadPlacesLibrary(locale)
        sessionToken.current ??= new AutocompleteSessionToken()

        const { suggestions } =
          await AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            language: locale,
            sessionToken: sessionToken.current,
          })

        callback(
          suggestions.flatMap(({ placePrediction }) =>
            placePrediction
              ? [{ label: placePrediction.text.text, value: placePrediction }]
              : []
          )
        )
      } catch (e: any) {
        Sentry.captureException(e)
        callback([])
      }
    },
    SEARCH_DEBOUNCE_MS
  )

  return (
    <AsyncSelect<PlaceOption>
      loadOptions={loadOptions}
      onChange={async (option) => {
        if (!option) {
          return
        }

        try {
          const { place } = await option.value
            .toPlace()
            .fetchFields({ fields: PLACE_FIELDS })
          // A new session starts after each selection (Places billing).
          sessionToken.current = undefined

          const a = {
            latitude: place.location?.lat(),
            longitude: place.location?.lng(),
          } as Address

          place.addressComponents?.forEach((component) => {
            if (component.types.includes('postal_code')) {
              a.postalCode = component.longText ?? undefined
            }

            if (component.types.includes('country')) {
              a.country = component.longText ?? undefined
              a.countryCode = component.shortText ?? undefined
            }

            if (component.types.includes('administrative_area_level_1')) {
              a.region = component.longText ?? undefined
            }

            if (component.types.includes('locality')) {
              a.city = component.longText ?? undefined
            }
          })

          a.formattedAddress = option.label
          a.address = option.value.mainText?.text
          setAddress(a)
        } catch (e: any) {
          Sentry.captureException(e)
          toast({
            title: 'Failed to setup the address, please try again',
            variant: 'destructive',
          })
        }
      }}
      isClearable
      placeholder={placeholder || t('start_typing', { ns: 'common' })}
      classNames={{
        menu: () => '!z-50',
        placeholder: () => '!text-sm !text-gray-500',
        control: () =>
          'flex h-10 w-full rounded-md border border-input bg-background px-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1',
      }}
      noOptionsMessage={() => t('no_result', { ns: 'common' })}
      loadingMessage={() => t('searching', { ns: 'common' })}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          text: 'white',
          primary25: 'whitesmoke',
          primary: '#004225',
        },
      })}
    />
  )
}

export default GoogleAutoComplete
